import { useEffect, useState } from 'react';
import { auth0Config } from '../Infrastructure/Auth/Auth0Config';

export const ADMIN = 'ADMIN';
export const CLIENTADMIN = 'CLIENTADMIN';
export const LEARNER = 'LEARNER';

const DEFAULT_AUTH_STATE = {
    isLoading: true,
    isAuthenticated: false,
    accessToken: null,
    user: null,
    error: null,
    auth0Available: false
};

let authState = DEFAULT_AUTH_STATE;
const listeners = new Set();
const authActions = {
    loginWithRedirect: async () => {
        throw new Error('Auth0 is not ready yet');
    },
    logout: async () => {
        throw new Error('Auth0 is not ready yet');
    },
    getAccessTokenSilently: async () => {
        throw new Error('Auth0 is not ready yet');
    },
    isConfigReady: false
};

const getNamespaces = () => {
    const namespace = auth0Config.namespace || 'https://your-app.com/';
    const normalizedNamespace = namespace.endsWith('/') ? namespace : `${namespace}/`;
    return Array.from(new Set([normalizedNamespace, 'https://academyone.com/']));
};

const getClaimCandidates = (claimName) => {
    return [
        ...getNamespaces().map(namespace => `${namespace}${claimName}`),
        claimName
    ];
};

const getFirstClaimValue = (source, keys) => {
    for (const key of keys) {
        const value = source?.[key];
        if (value !== undefined && value !== null && value !== '') {
            return value;
        }
    }

    return undefined;
};

const getClaimObject = (source, claimName) => {
    for (const key of getClaimCandidates(claimName)) {
        const value = source?.[key];
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            return value;
        }
    }

    return {};
};

const toRoleList = (value) => {
    if (!value) {
        return [];
    }

    if (Array.isArray(value)) {
        return value.filter(Boolean).map(item => String(item).toUpperCase());
    }

    return String(value)
        .split(/[\s,|;/]+/)
        .filter(Boolean)
        .map(item => item.toUpperCase());
};

const extractNameParts = (tokenPayload, profileUser = {}) => {
    const userMetadata = getClaimObject(tokenPayload, 'user_metadata');
    const givenName = userMetadata.first_name
        || userMetadata.firstName
        || tokenPayload?.given_name
        || profileUser?.given_name
        || profileUser?.firstName
        || '';
    const familyName = userMetadata.last_name
        || userMetadata.lastName
        || tokenPayload?.family_name
        || profileUser?.family_name
        || profileUser?.lastName
        || '';
    const fullName = profileUser?.name || tokenPayload?.name || [givenName, familyName].filter(Boolean).join(' ').trim();

    if (givenName || familyName) {
        return {
            firstName: givenName,
            lastName: familyName,
            displayName: [givenName, familyName].filter(Boolean).join(' ').trim()
        };
    }

    const fullNameParts = String(fullName).trim().split(/\s+/).filter(Boolean);
    return {
        firstName: fullNameParts[0] || '',
        lastName: fullNameParts.slice(1).join(' '),
        displayName: fullName
    };
};

const extractUserRoles = (tokenPayload, profileUser = {}) => {
    const appMetadata = getClaimObject(tokenPayload, 'app_metadata');
    const claims = [
        ...toRoleList(getFirstClaimValue(tokenPayload, getClaimCandidates('roles'))),
        ...toRoleList(getFirstClaimValue(tokenPayload, getClaimCandidates('role'))),
        ...toRoleList(getFirstClaimValue(tokenPayload, getClaimCandidates('secondary_role'))),
        ...toRoleList(getFirstClaimValue(tokenPayload, getClaimCandidates('secondaryRole')))
    ];
    const metadataRoles = toRoleList(appMetadata.roles || appMetadata.role || profileUser?.roles || profileUser?.role);
    const permissions = toRoleList(tokenPayload?.permissions || profileUser?.permissions);
    const combined = [...claims, ...metadataRoles, ...permissions]
        .filter(Boolean)
        .map(role => role.toUpperCase());

    return Array.from(new Set(combined));
};

export const normalizeAuth0User = (tokenPayload, accessToken, profileUser = {}) => {
    if (!tokenPayload && !profileUser) {
        return null;
    }

    const userMetadata = getClaimObject(tokenPayload, 'user_metadata');
    const appMetadata = getClaimObject(tokenPayload, 'app_metadata');
    const roles = extractUserRoles(tokenPayload, profileUser);
    const primaryRole = roles[0] || null;
    const secondaryRole = roles[1] || null;
    const nameParts = extractNameParts(tokenPayload, profileUser);
    const clientId = getFirstClaimValue(tokenPayload, getClaimCandidates('client_id'))
        || getFirstClaimValue(tokenPayload, getClaimCandidates('clientId'))
        || userMetadata.client_id
        || userMetadata.clientId
        || appMetadata.client_id
        || appMetadata.clientId
        || profileUser?.clientId
        || null;
    const urlToken = getFirstClaimValue(tokenPayload, getClaimCandidates('url_token'))
        || getFirstClaimValue(tokenPayload, getClaimCandidates('urlToken'))
        || userMetadata.url_token
        || userMetadata.urlToken
        || profileUser?.urlToken
        || null;
    const userId = getFirstClaimValue(tokenPayload, getClaimCandidates('user_id'))
        || getFirstClaimValue(tokenPayload, getClaimCandidates('userId'))
        || userMetadata.user_id
        || userMetadata.userId
        || appMetadata.user_id
        || appMetadata.userId
        || profileUser?.id
        || null;

    return {
        id: userId || tokenPayload?.sub || profileUser?.sub || profileUser?.id || null,
        sub: tokenPayload?.sub || profileUser?.sub || null,
        email: profileUser?.email || tokenPayload?.email || null,
        name: profileUser?.name || tokenPayload?.name || nameParts.displayName || profileUser?.email || tokenPayload?.email || null,
        firstName: userMetadata.first_name || userMetadata.firstName || nameParts.firstName || profileUser?.firstName || profileUser?.given_name || '',
        lastName: userMetadata.last_name || userMetadata.lastName || nameParts.lastName || profileUser?.lastName || profileUser?.family_name || '',
        role: primaryRole,
        secondaryRole,
        roles,
        clientId,
        clientName: appMetadata.client_name || appMetadata.clientName || profileUser?.clientName || null,
        urlToken,
        token: accessToken || null,
        expires: tokenPayload?.exp ? tokenPayload.exp * 1000 : null,
        picture: profileUser?.picture || tokenPayload?.picture || null
    };
};

export const setAuthActions = (actions) => {
    Object.assign(authActions, actions);
};

export const setAuthSnapshot = (nextState) => {
    authState = { ...authState, ...nextState };
    listeners.forEach(listener => listener(authState));
};

export const getAuthSnapshot = () => authState;

export const subscribeAuth = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

export const useAuthState = () => {
    const [state, setState] = useState(authState);

    useEffect(() => subscribeAuth(setState), []);

    return state;
};

export const isAuthReady = () => !authState.isLoading;

export function token() {
    return authState.accessToken || authState.user?.token || null;
}

export function getUsername() {
    return authState.user?.email || authState.user?.name || null;
}

export function isloggedIn() {
    return Boolean(authState.isAuthenticated && authState.user);
}

export function getRole() {
    return authState.user?.role || null;
}

export function userIsInRole(roleName) {
    if (!roleName || !authState.user) {
        return false;
    }

    const normalizedRole = String(roleName).toUpperCase();
    const roles = authState.user.roles || [];
    return roles.includes(normalizedRole)
        || String(authState.user.role || '').toUpperCase() === normalizedRole
        || String(authState.user.secondaryRole || '').toUpperCase() === normalizedRole;
}

export function getUserId() {
    return authState.user?.id || null;
}

export const getCurrentUser = () => authState.user;

export const loginWithAuth0 = async (options) => authActions.loginWithRedirect(options);

export const logoutWithAuth0 = async (options) => authActions.logout(options);

export const requestAccessToken = async (options) => authActions.getAccessTokenSilently(options);

export const isAuth0Configured = () => Boolean(authActions.isConfigReady);
